import type { MaybeRef } from 'vue';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { sanityImageWithAsset } from '@pkg/sanity-utils/sanityImageWithAsset';
import type { AuthorLocalised } from '@pkg/sanity-utils/types/author';
import { authorsPathSegment } from '../../../sanity/schemas/utils/slugs/slugConstants';
import {
  absolutePageUrlFromPath,
  injectJsonLdScript,
  resolveSeoTitle,
  useJsonLdShared,
} from './useJsonLdShared';

interface BlogPostingContent {
  _type?: string;
  title?: string;
  excerpt?: string;
  publishedAt?: string;
  updatedAt?: string;
  seoSocial?: {
    metaTitle?: string;
    metaDescription?: string;
    socialImage?: any;
  };
  /** Sanity imageWithAlt — field name is `image`, not `mainImage` */
  image?: any;
  slug?: string;
  author?: AuthorLocalised | null;
  language?: string;
}

/**
 * BlogPosting JSON-LD for standard About Food Waste blog posts.
 * Complements BreadcrumbList from usePostBreadcrumbJsonLd.
 *
 * Sanity fields (listingSingleFields): title, excerpt, publishedAt, updatedAt, image, author.
 * Author @id is only added when the author has a public bio page (Task 12).
 */
export function useBlogPostingJsonLd(
  content: MaybeRef<BlogPostingContent | null | undefined>,
) {
  const route = useRoute();
  const localePath = useLocalePath();
  const { $urlFor } = useNuxtApp();
  const { baseUrl, organizationId, localeValue } = useJsonLdShared();

  const jsonLd = computed(() => {
    const post = unref(content);
    if (!post || post._type !== DOCUMENT.ABOUT_FOOD_WASTE_BLOG) {
      return null;
    }

    const headline = resolveSeoTitle(post);
    if (!headline) {
      return null;
    }

    const pageUrl = absolutePageUrlFromPath(baseUrl.value, route.path);
    if (!pageUrl) {
      return null;
    }

    const articleId = `${pageUrl}#article`;
    const webpageId = `${pageUrl}#webpage`;
    const websiteId = `${baseUrl.value}/#website-${localeValue.value}`;

    const description = (
      post.excerpt ||
      post.seoSocial?.metaDescription ||
      ''
    ).trim();

    // Match visible byline in Posts.vue: publishedAt + optional CMS updatedAt only.
    const datePublished = post.publishedAt || undefined;
    const dateModified = post.updatedAt || post.publishedAt || undefined;

    const imageSource = sanityImageWithAsset(post.image) ??
      sanityImageWithAsset(post.seoSocial?.socialImage);
    const imageUrl = imageSource
      ? $urlFor(imageSource).width(1200).fit('max').quality(90).url()
      : undefined;

    const authorSlug = post.author?.slug?.current?.trim();
    const authorName = post.author?.name?.trim();
    // Only cross-ref when author page exists (hasBio) so @id resolves.
    const authorNode =
      authorName && authorSlug && post.author?.hasBio
        ? {
            author: {
              '@id': `${baseUrl.value}${localePath(`/${authorsPathSegment}/${authorSlug}`)}#author`,
            },
          }
        : {};

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': webpageId,
          url: pageUrl,
          name: headline,
          ...(description && { description }),
          isPartOf: { '@id': websiteId },
          mainEntity: { '@id': articleId },
        },
        {
          '@type': 'BlogPosting',
          '@id': articleId,
          headline,
          ...(description && { description }),
          ...(imageUrl && { image: imageUrl }),
          ...(datePublished && { datePublished }),
          ...(dateModified && { dateModified }),
          publisher: { '@id': organizationId.value },
          mainEntityOfPage: { '@id': webpageId },
          isPartOf: { '@id': websiteId },
          ...authorNode,
        },
      ],
    };
  });

  injectJsonLdScript('blog-blogposting-jsonld', jsonLd);
}
